import { CircleCheck } from '@/assets/icons/circle-check';
import { Combobox, ComboboxGroup, ComboboxItem } from '@/components/ui/combobox';
import { MemberAvatar } from '@/components/ui/member-avatar';
import { P3 } from '@/components/ui/text';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useState } from 'react';

interface AvailableOption {
  key: string;
  label?: string | null;
  extra?: { image_url?: string } | null;
}

interface AvailableOptionsValueInputProps {
  condition: string;
  value: string;
  onChange: (value: string) => void;
  autoOpen?: boolean;
  options: AvailableOption[];
  currentUserId?: string;
  /** Display name from the current user's session — used to label the
   *  current user's row as "{name} (you)" regardless of what label the
   *  options payload carries. */
  currentUserName?: string;
  showUserIcon?: boolean;
}

export function AvailableOptionsValueInput({
  value,
  onChange,
  autoOpen,
  options,
  currentUserId,
  currentUserName,
  showUserIcon = true,
}: AvailableOptionsValueInputProps) {
  const { t } = useTranslation('desktop_ui');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (autoOpen) {
      setOpen(true);
    }
  }, [autoOpen]);

  // Transform options: suffix current user's name with "(you)" and sort them first
  const transformedOptions = useMemo(() => {
    const youLabel = t('components.contacts.filter.availableOptions.you').toLowerCase();

    return options
      .map((opt) => {
        const isCurrentUser = opt.key === currentUserId;
        // For the current user, prefer the session name from `/v1/user` so the
        // dropdown always shows the logged-in identity — the option payload
        // sometimes carries "You" or a stale label that pre-dates the rename.
        const baseLabel = isCurrentUser
          ? (currentUserName ?? opt.label ?? opt.key)
          : (opt.label ?? opt.key);
        return {
          value: opt.key,
          label: isCurrentUser ? `${baseLabel} (${youLabel})` : baseLabel,
          isCurrentUser,
          imageUrl: opt.extra?.image_url,
        };
      })
      .filter((opt) => opt.value)
      .sort((a, b) => {
        // Current user ("You") always first
        if (a.isCurrentUser) {
          return -1;
        }
        if (b.isCurrentUser) {
          return 1;
        }
        return 0;
      });
  }, [options, currentUserId, currentUserName, t]);

  const selectedOption = transformedOptions.find((opt) => opt.value === value);
  const searchPlaceholder = t('components.contacts.filter.availableOptions.searchOption');
  const selectPlaceholder = t('components.contacts.filter.availableOptions.selectOption');
  const noOptionsText = t('components.contacts.filter.availableOptions.noOptions');

  const displayText = value ? selectedOption?.label || selectPlaceholder : selectPlaceholder;

  return (
    <Combobox
      variant="filter"
      searchPlaceholder={searchPlaceholder}
      options={transformedOptions}
      value={value}
      onValueChange={onChange}
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          type="button"
          className="h-full min-w-16 max-w-32 border-r border-primary-10 bg-transparent px-2 outline-none cursor-pointer hover:bg-primary-5 data-[state=open]:bg-primary-5 text-left truncate"
        >
          <P3 className={cn('truncate', value ? 'text-primary-100' : 'text-primary-60')}>
            {displayText}
          </P3>
        </button>
      }
    >
      {({ filteredOptions }) => (
        <ComboboxGroup variant="filter">
          {filteredOptions.length === 0 ? (
            <div className="flex items-center justify-center py-2">
              <P3 className="text-primary-60">{noOptionsText}</P3>
            </div>
          ) : (
            filteredOptions.map((option) => {
              const imageUrl = transformedOptions.find((o) => o.value === option.value)?.imageUrl;
              return (
                <ComboboxItem key={option.value} option={option} variant="filter">
                  {(opt, isSelected) => (
                    <div className="flex w-full items-center justify-between gap-1">
                      <div className="flex min-w-0 items-center gap-1">
                        {showUserIcon && <MemberAvatar name={opt.label} imageUrl={imageUrl} />}
                        <P3 className="truncate text-primary-100">{opt.label}</P3>
                      </div>
                      {isSelected && <CircleCheck className="size-4 text-highlight-default" />}
                    </div>
                  )}
                </ComboboxItem>
              );
            })
          )}
        </ComboboxGroup>
      )}
    </Combobox>
  );
}
